import { BigNumber } from "bignumber.js";
import padStart from "lodash.padstart";
import moment from "moment";
export function getFormattedNumber(value, locale) {
    const formatter = new Intl.NumberFormat(locale);
    return formatter.format(value);
}
export function getFormattedInteger(value) {
    return String(Math.floor(value));
}
export function getEditableInteger(value) {
    if (value === 0) {
        return "";
    }
    else {
        return getFormattedInteger(value);
    }
}
export function getFormattedFloat(value, locale, step, options) {
    if (step) {
        const decimalPlaces = calculateDecimalPlaces(step);
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimalPlaces,
        }).format(Math.round(value / step) * step);
    }
    return new Intl.NumberFormat(locale, options ?? {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
}
export function getEditableFloat(value, locale, returnEmptyStringIfZero = true, step) {
    if (returnEmptyStringIfZero && value === 0) {
        return "";
    }
    const separator = new Intl.NumberFormat(locale)
        .format(1111)
        .replace(/1/g, "");
    if (step) {
        const decimalPlaces = calculateDecimalPlaces(step);
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimalPlaces,
        })
            .format(Math.round(value / step) * step)
            .replace(new RegExp(`\\${separator}`, "g"), "");
    }
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })
        .format(value)
        .replace(new RegExp(`\\${separator}`, "g"), "");
}
export function getFloat(value, locale, returnZeroIfNaN = true) {
    const separator = new Intl.NumberFormat(locale)
        .format(1111)
        .replace(/1/g, "");
    const decimal = new Intl.NumberFormat(locale).format(1.1).replace(/1/g, "");
    const numericValue = value.replace(/[^\d.,-]/g, "");
    const formatedValue = numericValue
        .replace(new RegExp(`\\${separator}`, "g"), "")
        .replace(new RegExp(`\\${decimal}`, "g"), ".");
    const numberValue = parseFloat(formatedValue);
    if (Number.isNaN(numberValue) && returnZeroIfNaN)
        return 0;
    return numberValue;
}
export function getFormattedPercentage(value, locale) {
    if (BigNumber.isBigNumber(value)) {
        return getFormattedFloat(value.toNumber() * 100, locale) + "%";
    }
    return getFormattedFloat(value * 100, locale) + "%";
}
export function calculateDecimalPlaces(step) {
    if (Number.isInteger(step)) {
        return 0;
    }
    const stepStr = step.toString();
    if (stepStr.includes(".")) {
        const decimalPart = stepStr.split(".")[1];
        return decimalPart.length;
    }
    if (stepStr.includes("e-")) {
        return parseInt(stepStr.split("e-")[1], 10);
    }
    return 0;
}
export var Currency;
(function (Currency) {
    Currency["Eur"] = "EUR";
})(Currency || (Currency = {}));
export function getFormattedPrice(value, locale) {
    if (value === undefined || value === null)
        return;
    const currency = Currency.Eur;
    const formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return formatter.format(value).replace(currency, getCurrencySymbol(currency));
}
function getCurrencySymbol(currency) {
    switch (currency) {
        case Currency.Eur:
            return "€";
    }
}
export function getFormattedDate(value, locale) {
    return moment(value).locale(locale).format("L");
}
export function getFormattedDateWithoutYear(value, locale) {
    return moment(value).locale(locale).toDate().toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
    });
}
export function getFormattedDateTime(value, locale) {
    return `${moment(value).locale(locale).format("L")} ${moment(value)
        .locale(locale)
        .format("LT")}`;
}
export function getFormattedTime(value) {
    const hours = Math.floor(value / 60);
    const min = Math.round(value - hours * 60);
    return { hours, min };
}
export function getFormattedDuration(durationInMinutes) {
    if (durationInMinutes === undefined || durationInMinutes === null)
        return durationInMinutes;
    const hours = Math.floor(Math.abs(durationInMinutes) / 60);
    const minutes = Math.floor(Math.abs(durationInMinutes) % 60);
    return ((durationInMinutes < 0 ? "−" : "") +
        `${hours}:${padStart(minutes.toString(), 2, "0")}`);
}
export function getHumanReadableDuration(durationInSeconds, locale) {
    // Default version
    // return moment.duration(durationInSeconds * 1000).locale(locale).humanize();
    // Version matching our duration select in UI
    const localeData = moment.localeData(locale);
    const [durationAmount, durationType] = getNormalizedDuration(durationInSeconds);
    switch (durationType) {
        case "hours":
            return localeData.relativeTime(durationAmount, true, durationAmount > 1 ? "hh" : "h", true);
        case "days":
            return localeData.relativeTime(durationAmount, true, durationAmount > 1 ? "dd" : "d", true);
        case "weeks":
            return localeData.relativeTime(durationAmount, true, durationAmount > 1 ? "ww" : "w", true);
        case "months":
            return localeData.relativeTime(durationAmount, true, durationAmount > 1 ? "MM" : "M", true);
        default:
            return localeData.invalidDate();
    }
}
const oneHour = 60 * 60;
const oneDay = 24 * oneHour;
const oneWeek = 7 * oneDay;
const oneMonth = 31 * oneDay;
export const getDurationInSeconds = (duration) => {
    switch (duration.durationType) {
        case "hours":
            return duration.durationAmount * oneHour;
        case "days":
            return duration.durationAmount * oneDay;
        case "weeks":
            return duration.durationAmount * oneWeek;
        case "months":
            return duration.durationAmount * oneMonth;
        default:
            return 0;
    }
};
export const getNormalizedDuration = (duration) => {
    if (!duration) {
        return [0, "none"];
    }
    if (duration % oneMonth === 0) {
        return [duration / (oneMonth * 1.0), "months"];
    }
    if (duration % oneWeek === 0) {
        return [duration / (oneWeek * 1.0), "weeks"];
    }
    if (duration % oneDay === 0) {
        return [duration / (oneDay * 1.0), "days"];
    }
    return [duration / (oneHour * 1.0), "hours"];
};
// extracts hours:minutes from time string returned from db: '06:00:00+02' -> '06:00'
export const parseTime = (time) => {
    const m = moment.parseZone(time, "HH:mm:ssZZ");
    if (!m.isValid())
        return null;
    return [m.hours(), m.minutes(), m.seconds(), m.utcOffset()];
};
export function normalizeTime(time) {
    if (time === null || time === undefined)
        return undefined;
    return (time.split(":")[0].padStart(2, "0") +
        ":" +
        time.split(":")[1].padStart(2, "0"));
}
//# sourceMappingURL=formatting.js.map